import { Button } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Switch } from '@neo/primitives/switch'
import { useQuery } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { useState } from 'react'
import { handoffContextService } from 'v2/shared/services/handoff-context-service'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import { ConnectionDialog } from './handoff-connection-dialog'
import { NOTE_FIELDS, type NoteField, toggleNoteField } from './handoff-context.helpers'
import type { QNRFieldRendererProps, QNRFormValues } from './types'

type ContextPolicy = NonNullable<QNRFormValues['context']>

const DEFAULT_POLICY: ContextPolicy = { enabled: false, sources: ['external'], fields: [] }

/** Context section inside Assign to human: this node's policy plus the organization's AI agent.
 * A `basic` field gets no field subscription from FieldsRenderer, so the section subscribes itself. */
export const HandoffContext = ({ form }: Pick<QNRFieldRendererProps, 'form'>) => (
  <form.Subscribe selector={(state) => state.values.context}>
    {(context) => <ContextFields form={form} context={context || DEFAULT_POLICY} />}
  </form.Subscribe>
)

const ContextFields = ({
  form,
  context,
}: Pick<QNRFieldRendererProps, 'form'> & { context: ContextPolicy }) => {
  const update = (patch: Partial<ContextPolicy>) =>
    form.setFieldValue('context', { ...context, ...patch })
  const isShown = (key: NoteField) => !context.fields.length || context.fields.includes(key)
  const shownCount = NOTE_FIELDS.filter((f) => isShown(f.key)).length

  return (
    <div className="flex flex-col gap-5" data-testid="qnr-context-section">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <label htmlFor="context.enabled" className="text-p-500 text-primary">
            Include transfer context
          </label>
          <span className="text-s-400 text-secondary">
            Show what your AI agent shared before the call was transferred.
          </span>
        </div>
        <Switch
          id="context.enabled"
          name="context.enabled"
          value={context.enabled}
          onChange={(on) => update({ enabled: on, sources: ['external'] })}
        />
      </div>
      {context.enabled && (
        <>
          <div className="flex flex-col gap-2">
            <span className="text-s-500 text-primary">Include in transfer context</span>
            <div className="flex flex-wrap gap-2">
              {NOTE_FIELDS.map(({ key, label }) => {
                const on = isShown(key)
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={on}
                    // at least one part stays on; the switch above turns the context off
                    disabled={on && shownCount === 1}
                    data-testid={`qnr-context-field-${key}`}
                    onClick={() => update({ fields: toggleNoteField(context.fields, key) })}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-s-400 transition-colors disabled:cursor-not-allowed',
                      on
                        ? 'border-brand bg-brand/5 text-primary'
                        : 'border-st2 text-secondary hover:bg-hv1',
                    )}
                  >
                    {on && <Check className="size-3.5 text-brand-text" aria-hidden />}
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
          <AgentCard />
        </>
      )}
    </div>
  )
}

const AgentCard = () => {
  const [open, setOpen] = useState(false)
  const { data: connection } = useQuery({
    queryKey: conversationQueryKeys().handoffContextConnection,
    queryFn: async () => (await handoffContextService.getConnection()).data,
    retry: false,
    staleTime: 60_000,
  })
  if (!connection) return null

  return (
    <div className="flex flex-col gap-2">
      <span className="text-s-500 text-primary">
        AI agent <span className="text-xs-400 text-tertiary">shared by all Flows</span>
      </span>
      <div
        className="flex items-center justify-between gap-3 rounded-md border border-st2 p-3"
        data-testid="qnr-context-agent"
      >
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-2 text-s-500 text-primary">
            <span
              className={cn(
                'size-2 shrink-0 rounded-full',
                connection.has_key ? 'bg-status-success' : 'bg-status-warning',
              )}
              aria-hidden
            />
            <span className="truncate">
              {connection.has_key ? connection.name : 'Not connected'}
            </span>
          </span>
          <span className="text-xs-400 text-secondary">
            {connection.has_key
              ? 'API key set'
              : 'Get an API key and setup steps for your developer.'}
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          variant={connection.has_key ? 'outline' : 'default'}
          onClick={() => setOpen(true)}
        >
          {connection.has_key ? 'Manage' : 'Connect'}
        </Button>
      </div>
      <ConnectionDialog open={open} onOpenChange={setOpen} connection={connection} />
    </div>
  )
}
